<template>
  <AppWithLoading v-slot:default="{loader}">
    <VResponsive :aspect-ratio="`16/9:${$vuetify.breakpoint.mdAndUp}`" class="overflow-hidden">
      <template v-if="hasInterviews && !openInterviewCreate">
        <div
          class="card-actions-top"
          :class="{ 'card-actions-top--blurred': isQuotaExceeded }"
        >
          <button
            class="card-actions-top__btn mb-sm-0 mb-lg-0"
            @click.stop="openInterviewCreatePage"
          >
            {{ $t('nextStep') }}
          </button>
          <VSelect
            class="card-actions-top__btn card-actions-top__btn_select"
            v-model="interview"
            :items="interviews"
            item-text="label"
            return-object
            hide-details
            flat
            dense
            background-color="#f5f5f5"
            height="20px"
          />
        </div>
      </template>
      <template v-if="hasInterviews && !openInterviewCreate">
        <CandidateInterview
          :candidate="candidate"
          :interview="selectedInterview"
          :vacancy="vacancy"
          :key="candidateInterviewKey"
          :are-questions-equal="areQuestionsEqual"
          @online-interview-played="$emit('online-interview-played')"
        />
      </template>
      <template v-else>
        <CreateInterviewPage
          v-if="create === null"
          :candidate="candidate"
          @show-last="findLatsAndShow($event)"
          @office-interview="recordOfficeInterview"
          @online-interview="loader(() => inviteToOnlineInterviewAndUpdateInterview(candidate.id))"
          @video-call="loader(() => storeVideoCallAndUpdateInterview(candidate.id))"
          @uploaded-video="createUploadedVideoInterview"
          @cancel="closeInterviewCreate"
        />
        <CreateUploadedVideoInterview
          v-else-if="create === candidateInterviewTypes.UPLOADED_VIDEO"
          :candidate="candidate"
          @cancel="closeInterviewCreate"
          @created="uploadedVideoInterviewCreated"
        />
      </template>
    </VResponsive>
    <VDialog
      v-if="officeInterviewDialog"
      v-model="officeInterviewDialog"
      :key="key"
      persistent
      :max-width="900"
    >
      <VCard :key="key">
        <DialogHeader v-if="showOfficeInterviewCloseButton" @close="closeOfficeInterviewDialog" />
        <VCardText>
          <OfficeInterviewRecorderForm
            :key="key"
            :vacancy="vacancy"
            :candidate="candidate"
            :resume="officeInterviewResume"
            :overwrite="officeInterviewOverwrite"
            @started="interviewRecordingStarted"
            @stopped="interviewRecordingFinished"
            @error="interviewRecordingError"
          />
        </VCardText>
      </VCard>
    </VDialog>
  </AppWithLoading>
</template>

<script>
import Interview from '@/orm/models/Interview/Interview';
import AppWithLoading from '@/components/AppWithLoading';
import CreateInterviewPage from '@/components/Candidate/Interview/CreateInterviewPage';
import useCandidates from '@/compositions/useCandidates';
import {
  interviewType,
  candidateInterviewType,
} from '@/enums';
import moment from 'moment';
import { useI18n } from 'vue-i18n-composable';
import CandidateInterview from '@/components/Candidate/Interview/CandidateInterview';
import OfficeInterview from '@/orm/models/OfficeInterview/OfficeInterview';
import CreateUploadedVideoInterview from '@/components/Candidate/Interview/CreateUploadedVideoInterview';
import { clearGlobalStream } from '@huntica/huntica-interview-module/src/mediaHelpers';
import ValidationMixin from '../../mixins/ValidationMixin';
import OfficeInterviewRecorderForm from '../OfficeInterview/OfficeInterviewRecorderForm.vue';
import DialogHeader from '../DialogHeader.vue';

export default {
  name: 'CandidateInterviewResolver',
  components: {
    CreateUploadedVideoInterview,
    CandidateInterview,
    CreateInterviewPage,
    AppWithLoading,
    OfficeInterviewRecorderForm,
    DialogHeader,
  },
  mixins: [ValidationMixin],
  props: {
    vacancy: {
      type: Object,
      required: true,
    },
    candidate: {
      type: Object,
      required: true,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    areQuestionsEqual: {
      type: Boolean,
      required: true,
      default: true,
    },
    isQuotaExceeded: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const { locale, t } = useI18n();
    const {
      inviteToOnlineInterview,
      storeVideoCall,
      showCandidateInterviewType,
      getLastOfficeInterviewByType,
    } = useCandidates();

    function getLabelName(type, momentValue) {
      if (locale.value.toLowerCase() === 'ru') {
        return `${t(type)} (${momentValue.format('DD.MM.YYYY HH:mm:ss')})`;
      }

      return `${t(type)} (${momentValue.format('MM/DD/YYYY HH:mm:ss')})`;
    }

    return {
      locale: locale.value.toLowerCase(),
      inviteToOnlineInterview,
      getLabelName,
      storeVideoCall,
      showCandidateInterviewType,
      getLastOfficeInterviewByType,
    };
  },
  data() {
    return {
      interviewTypeEnum: interviewType,
      officeInterviewDialog: false,
      officeInterviewRecording: false,
      officeInterviewRecorded: false,
      officeInterviewResume: false,
      officeInterviewOverwrite: false,
      officeInterviewPrepareLoading: false,
      showOfficeInterviewCloseButton: true,
      errorWhileOpen: false,
      openInterviewCreate: false,
      interview: null,
      candidateInterviewKey: null,
      candidateInterviewTypes: candidateInterviewType,
      create: null,
      localShowCandidateInterviewType: null,
    };
  },
  computed: {
    key() {
      return Date.now();
    },
    interviews() {
      const interviews = [];

      this.candidate.officeInterviews.forEach((officeInterview) => {
        officeInterview.createdAt = moment(officeInterview.createdAt);
        officeInterview.label = this.getLabelName(officeInterview.type, officeInterview.createdAt);
        interviews.push(officeInterview);
      });
      const onlineInterview = this.candidate?.interview;

      if (onlineInterview) {
        onlineInterview.type = candidateInterviewType.ONLINE_INTERVIEW;
        onlineInterview.createdAt = moment(onlineInterview.createdAt);
        onlineInterview.isInProgress = false;
        onlineInterview.label = this.getLabelName(
          candidateInterviewType.ONLINE_INTERVIEW,
          onlineInterview.createdAt,
        );
        interviews.push(onlineInterview);
      } else if (!this.candidate.canCreateOnlineInterview) {
        const inProcessOnlineInterview = {
          type: candidateInterviewType.ONLINE_INTERVIEW,
          createdAt: moment('01/01/1900', 'DD/MM/YYYY'),
          isInProgress: true,
          label: this.$t('online_interview'),
        };
        this.candidate.interview = inProcessOnlineInterview;
        interviews.push(inProcessOnlineInterview);
      }

      return interviews.sort((previous, next) => (
        (previous.createdAt.unix() > next.createdAt.unix())
          ? -1
          : ((next.createdAt.unix() > previous.createdAt.unix()) ? 1 : 0)
      ));
    },
    selectedInterview() {
      if (!this.interview) return null;

      if (this.interview.type === candidateInterviewType.ONLINE_INTERVIEW) {
        const onlineId = this.candidate.interview?.id || this.interview?.id;

        const selectedInterview = Interview.query()
          .withAllRecursive()
          .where('candidateId', this.candidate.id)
          .where('id', onlineId)
          .first() || this.interview;

        if (selectedInterview?.id) {
          selectedInterview.createdAt = this.interview.createdAt;
          selectedInterview.label = this.interview.label;
          selectedInterview.isInProgress = this.interview.isInProgress;
        }

        selectedInterview.type = this.interview.type;

        return selectedInterview;
      }

      const selectedInterview = OfficeInterview.query()
        .withAllRecursive()
        .where('candidateId', this.candidate.id)
        .where('id', this.interview.id)
        .first() || this.interview;

      if (selectedInterview) {
        selectedInterview.createdAt = moment(this.interview.createdAt);
        selectedInterview.label = this.interview.label;
        selectedInterview.type = this.interview.type;
      }

      return selectedInterview;
    },
    hasInterviews() {
      return !!this.candidate.officeInterviews?.length > 0
        || !this.candidate.canCreateOnlineInterview
        || this.candidate?.interview;
    },
    interviewType: {
      get() {
        return this.$store.state.candidatePage.activeInterviewType;
      },
      set(value) {
        this.$store.commit('candidatePage/setActiveInterviewType', value);

        if (this.$route.query.tab !== 'contact') {
          this.$store.commit('candidatePage/setActiveTab', 0);
        }
      },
    },
  },
  watch: {
    interview(newValue) {
      this.candidateInterviewKey = Date.now();
    },
  },
  created() {
    this.interviewType = this.resolveInitialInterviewType();
    const showCandidateInterviewType = this.$route.query.lastInterviewType;
    if (showCandidateInterviewType) {
      this.findLatsAndShow(showCandidateInterviewType);
    } else {
      this.setFirstInterview();
    }

    this.candidateInterviewKey = Date.now();
  },
  beforeDestroy() {
    clearGlobalStream();
  },
  methods: {
    async inviteToOnlineInterviewAndUpdateInterview(id) {
      await this.inviteToOnlineInterview(id);
      this.setFirstInterview();
    },
    async storeVideoCallAndUpdateInterview(id) {
      await this.storeVideoCall(id);
      this.setFirstInterview();
    },
    setFirstInterview() {
      this.interview = this.getFirstInterview();
    },
    getFirstInterview() {
      return this.interviews.length > 0 ? this.interviews[0] : null;
    },
    tryOpenInterview() {
      try {
        Interview.api().open(this.candidate.interview.id);
      } catch (e) {
        this.errorWhileOpen = true;
      }
    },
    resolveInitialInterviewType() {
      if (this.candidate.interview && this.candidate.officeInterview) {
        return interviewType.ONLINE_INTERVIEW;
      }
      if (this.candidate.interview) {
        return interviewType.ONLINE_INTERVIEW;
      }
      if (this.candidate.officeInterview) {
        return interviewType.OFFICE_INTERVIEW;
      }
      return this.candidate.invitationType;
    },
    showOfficeInterviewDialog() {
      this.officeInterviewDialog = true;
    },
    closeOfficeInterviewDialog() {
      this.officeInterviewDialog = false;
      clearGlobalStream();
    },
    recordOfficeInterview() {
      this.officeInterviewOverwrite = false;
      this.officeInterviewResume = false;
      this.showOfficeInterviewDialog();
    },
    interviewRecordingStarted() {
      this.officeInterviewRecording = true;
      this.showOfficeInterviewCloseButton = false;
    },
    interviewRecordingFinished() {
      this.officeInterviewDialog = false;
      this.officeInterviewRecording = false;
      this.officeInterviewRecorded = true;
      this.showOfficeInterviewCloseButton = true;
      this.setFirstInterview();
    },
    interviewRecordingError() {
      this.officeInterviewDialog = false;
      this.officeInterviewRecording = false;
      this.showOfficeInterviewCloseButton = true;
    },
    openInterviewCreatePage() {
      this.openInterviewCreate = true;
    },
    closeInterviewCreate() {
      this.openInterviewCreate = false;
      this.create = null;
    },
    createUploadedVideoInterview() {
      this.create = this.candidateInterviewTypes.UPLOADED_VIDEO;
    },
    uploadedVideoInterviewCreated() {
      this.setFirstInterview();
      this.closeInterviewCreate();
    },
    changeShowOfficeInterview(newId) {
      const showInterview = this.interviews.find(
        (interview) => interview.id === newId
          && interview.type !== candidateInterviewType.ONLINE_INTERVIEW,
      );

      if (!showInterview) {
        return;
      }

      if (showInterview.id === this.interview.id) {
        return;
      }

      this.interview = showInterview;
    },
    changeShowOnOnlineInterview() {
      const showInterview = this.interviews.find(
        (interview) => interview.type === candidateInterviewType.ONLINE_INTERVIEW,
      );

      if (!showInterview) {
        return;
      }

      if (showInterview.id === this.interview.id) {
        return;
      }

      this.interview = showInterview;
    },
    findInterviewByIdAndType(id, type) {
      const searched = this.interviews.filter(
        (interview) => interview.type === type && interview.id === id,
      );
      if (!searched) {
        return null;
      }

      return searched[0];
    },
    findLatsAndShow(showCandidateInterviewType) {
      if (showCandidateInterviewType === candidateInterviewType.ONLINE_INTERVIEW) {
        const searchedInterview = this.interviews.find((interview) => interview.type === showCandidateInterviewType);

        if (!searchedInterview) {
          this.findLatsAndShow();
          return;
        }

        this.interview = searchedInterview;

        return;
      }

      const searchedInterview = this.getLastOfficeInterviewByType(
        this.candidate,
        showCandidateInterviewType,
      );

      if (!searchedInterview) {
        this.findLatsAndShow();
        return;
      }

      this.interview = this.findInterviewByIdAndType(
        searchedInterview.id,
        showCandidateInterviewType,
      );
    },
  },
};
</script>
<style lang="scss" scoped>
  .card-actions-top--blurred {
    filter: blur(4px);
    pointer-events: none;
    user-select: none;
  }

  .card-actions-top {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0;
    background-color: #f5f5f5;
    border-radius: 0.5rem 0.5rem 0 0;
    border-bottom: 1px solid #E0E0E0;
    &__btn {
      min-width: 8.75rem;
      font-size: 0.75rem;
      color: #757575;
      background-color: #f5f5f5;
      border-radius: 0.25rem;
      border: 1px solid #E0E0E0;
      padding: 0.3125rem 0.625rem;
      margin-right: 1.25rem;
      &_select {
        max-width: 14.875rem;
        padding: 0.25rem 0.25rem 0.25rem 0.625rem;
        margin-right: 0;
        margin-top: 0;
      }
    }
    @media screen and (max-width: 599px) {
      flex-wrap: wrap;
      gap: 5px;
    }
  }
  .interview-switch {
    &::v-deep {
      .success--text {
        color: map-get($light-blue, 'darken-3') !important;
        caret-color: map-get($light-blue, 'darken-3') !important;
      }
    }
  }
</style>

<i18n locale="en" lang="yaml">
  recordOfficeInterview: Record in-person interview
  recordWasStopped: Recording was interrupted
  chooseOption: Choose an option
  resumeOfficeInterview: Continue recording
  overwrite: Overwrite
  save: Save
  interviewOnline: Online interview
  officeInterview: In-person interview
  nextStep: Next step
  online_interview: Virtual interview
  video_call: Video call
  office_interview: Office meeting
  uploaded_video: Video file
</i18n>

<i18n locale="ru" lang="yaml">
  recordOfficeInterview: Записать очное интервью
  recordWasStopped: По какой-то причине запись была прервана
  chooseOption: Выберите один из подходящих вариантов
  resumeOfficeInterview: Продолжить запись
  overwrite: Записать заново
  save: Сохранить
  interviewOnline: Онлайн интервью
  officeInterview: Очное интервью
  nextStep: Создать новый этап
  online_interview: Автоматическое интервью
  video_call: Видеозвонок
  office_interview: Встреча в офисе
  uploaded_video: Запись
</i18n>
